import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import ConversationsList from '@/components/messages/ConversationsList';
import MessageDetail from '@/components/messages/MessageDetail';
import EmptyConversationState from '@/components/messages/EmptyConversationState';
import BookingDialog from '@/components/messages/BookingDialog';
import { UIMessage } from '@/hooks/useMessages';
import { Conversation } from '@/hooks/useConversations';
import { useLanguage } from '@/context/language-context';
import { Loader2 } from 'lucide-react';

interface MessagesMobileViewProps {
  userId?: string;
  chatPartner: any | null;
  partnerDetails?: any;
  messages: UIMessage[];
  isLoadingMessages: boolean;
  sendMessage: (message: string) => Promise<void>;
  deleteMessage?: (messageId: string) => Promise<void>;
  onArchiveConversation?: (conversationId: string) => Promise<void>;
  onSelectConversation: (conversationId: string) => void;
  isBookingDialogOpen: boolean;
  setIsBookingDialogOpen: (open: boolean) => void;
  handleBookAppointment: (date: Date, timeSlot: string, duration: number) => Promise<void>;
  handleBackToList: () => void;
  conversations: Conversation[];
  isLoadingConversations: boolean;
  activeConversation: Conversation | null;
}

const MessagesMobileView: React.FC<MessagesMobileViewProps> = ({
  userId,
  chatPartner,
  partnerDetails,
  messages,
  isLoadingMessages,
  sendMessage,
  deleteMessage,
  onArchiveConversation,
  onSelectConversation,
  isBookingDialogOpen,
  setIsBookingDialogOpen,
  handleBookAppointment,
  handleBackToList,
  conversations,
  isLoadingConversations,
  activeConversation
}) => {
  const [showConversationList, setShowConversationList] = useState(!userId || !chatPartner);
  const { t } = useLanguage();

  useEffect(() => {
    // If user ID and chat partner are set, show the message detail view
    if (userId && chatPartner) {
      setShowConversationList(false);
    } else {
      setShowConversationList(true);
    }
  }, [userId, chatPartner]);

  // If loading messages and we're supposed to show the message detail view, show a loading spinner
  if (isLoadingMessages && userId && !showConversationList) {
    return (
      <div className="py-4 px-4 bg-background min-h-screen max-w-4xl mx-auto flex flex-col">
        <Card className="shadow-md overflow-hidden border-pink-100 flex-grow flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-pink-500" />
            <p className="text-gray-600">{t('messages.loading_conversation')}</p>
          </div>
        </Card>
      </div>
    );
  }

  // If we're showing the conversation list or there's no user ID or chat partner, show the conversation list
  if (showConversationList || !userId || !chatPartner) {
    return (
      <div className="py-4 px-4 bg-background min-h-screen max-w-4xl mx-auto flex flex-col">
        <h1 className="text-2xl font-bold mb-4 shrink-0">{t('messages.title')}</h1>
        <Card className="shadow-md overflow-hidden border-pink-100 flex-grow flex flex-col">
          <ConversationsList 
            conversations={conversations}
            isLoading={isLoadingConversations}
            activeConversationId={activeConversation?.id}
            onSelectConversation={(conversationId) => {
              onSelectConversation(conversationId);
              setShowConversationList(false);
            }}
            onArchiveConversation={onArchiveConversation}
          />
        </Card>
      </div>
    );
  }

  // If we have messages and a chat partner, show the message detail view
  return (
    <div className="py-4 px-4 bg-background min-h-screen max-w-4xl mx-auto flex flex-col">
      <Card className="shadow-md overflow-hidden border-pink-100 flex-grow flex flex-col">
        <MessageDetail
          chatPartner={chatPartner}
          messages={messages}
          isLoading={isLoadingMessages}
          onSendMessage={sendMessage}
          deleteMessage={deleteMessage}
          onOpenBookingModal={() => setIsBookingDialogOpen(true)}
          userId={userId}
          isMobile={true}
          onBack={() => setShowConversationList(true)}
        />
      </Card>
      
      <BookingDialog
        isOpen={isBookingDialogOpen}
        onOpenChange={setIsBookingDialogOpen}
        onBookAppointment={handleBookAppointment}
        escortId={chatPartner?.id || ''}
      />
    </div>
  );
};

export default MessagesMobileView;
